import { computed, effect, inject, Injectable, signal } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'

export interface TranslationConfig {
  defaultLanguage: string
  supportedLanguages: string[]
  fallbackLanguage: string
}

export interface LanguageOption {
  code: string
  name: string
  menuLabel: string
  nativeName: string
  direction: 'ltr' | 'rtl'
}

export const LANGUAGE_OPTIONS: LanguageOption[] = [
  { code: 'en', name: 'English', menuLabel: 'EN', direction: 'ltr', nativeName: '' },
  { code: 'ar', name: 'Arabic', menuLabel: 'AR', direction: 'rtl', nativeName: 'العربية' }
]

export const LANGUAGE_CONFIG: TranslationConfig = {
  defaultLanguage: LANGUAGE_OPTIONS[0].code,
  supportedLanguages: LANGUAGE_OPTIONS.map((lang) => lang.code),
  fallbackLanguage: LANGUAGE_OPTIONS[0].code
}

@Injectable({
  providedIn: 'root'
})
export class V2TranslationService {
  private readonly translate = inject(TranslateService)
  private _currentLang = signal<LanguageOption>(null)
  readonly currentLang = computed(() => this._currentLang())

  constructor() {
    this.translate.addLangs(LANGUAGE_CONFIG.supportedLanguages)
    this.translate.setDefaultLang(LANGUAGE_CONFIG.defaultLanguage)

    const storedLang = this._getStoredLang()
    this._currentLang.set(storedLang) // triggers effect

    effect(() => this._applyLanguageChanges(this._currentLang()))
  }

  setLanguage(lang: LanguageOption): void {
    this._applyLanguageChanges(lang)
  }

  get supportedLanguages(): LanguageOption[] {
    return LANGUAGE_OPTIONS
  }

  private _applyLanguageChanges(lang: LanguageOption): void {
    localStorage.setItem('langCode', lang.code)
    this._currentLang.set(lang)
    this.translate.use(lang.code)
    document.documentElement.dir = lang.direction
  }

  private _getStoredLang(): LanguageOption {
    const stored = localStorage.getItem('langCode')
    return LANGUAGE_OPTIONS.find((lang) => lang.code === stored) ?? LANGUAGE_OPTIONS[0]
  }

  /**
   * Checks if current language is RTL
   * @returns boolean - True if RTL language
   */
  public isRTL(): boolean {
    return document.documentElement.dir === 'rtl'
  }
}
